import React, { useCallback, useState } from "react";
import {
  AnimatePresence,
  LazyMotion,
  domAnimation,
  m,
  useReducedMotion,
} from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  AppWindow,
  ArrowUpRight,
  Boxes,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  MessagesSquare,
  UserCog,
} from "lucide-react";
import CountUp from "./CountUp";
import { blogUrl } from "../../lib/siteUrl";
import {
  testimonialIntegrations,
  testimonialIntegrationsLead,
  testimonials,
  testimonialsIntro,
  type IntegrationItem,
} from "./testimonials";

const easeOut = [0.22, 1, 0.36, 1] as const;

const headerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.04 },
  },
};

const headerChild = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOut },
  },
};

function parseMetric(metric: string) {
  const match = metric.trim().match(/^(-?\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  return { value: Number(match[1]), suffix: match[2] };
}

const INTEGRATION_ICONS: Record<IntegrationItem["icon"], LucideIcon> = {
  messages: MessagesSquare,
  site: AppWindow,
  crm: UserCog,
  order: ClipboardList,
  stock: Boxes,
  booking: CalendarCheck,
};

export default function TestimonialsSection() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const count = testimonials.length;
  const current = testimonials[index];
  const parsedMetric = parseMetric(current.metric);
  const canCycle = count > 1;

  const goTo = useCallback(
    (next: number, dir: number) => {
      if (!canCycle) return;
      setDirection(dir);
      setIndex((next + count) % count);
    },
    [canCycle, count],
  );

  const slideOffset = reduceMotion ? 0 : 28;

  return (
    <section
      id="testimonials"
      className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28 lg:px-8 lg:py-36"
    >
      <LazyMotion features={domAnimation}>
        <m.div
          className="relative mb-10 flex flex-col items-center text-center sm:mb-12"
          variants={headerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10% 0px" }}
        >
          <m.p
            variants={headerChild}
            className="mb-3 flex items-center justify-center gap-2 text-[13px] font-medium tracking-wide text-[#00b7ff] sm:text-sm"
          >
            <span
              className="size-1.5 shrink-0 rounded-full bg-[#00b7ff] shadow-[0_0_8px_rgba(0,183,255,0.65)]"
              aria-hidden
            />
            {testimonialsIntro.kicker}
          </m.p>
          <m.h2
            variants={headerChild}
            className="text-foreground max-w-[18ch] text-balance text-[1.65rem] font-bold leading-[1.25] tracking-tight sm:max-w-none sm:text-3xl sm:leading-[1.2] lg:text-[2rem]"
          >
            {testimonialsIntro.title}
          </m.h2>
          <m.p
            variants={headerChild}
            className="text-default-500 mt-4 max-w-[46ch] text-pretty text-[15px] leading-relaxed sm:text-base"
          >
            {testimonialsIntro.body}
          </m.p>
        </m.div>

        <m.div
          role="region"
          aria-roledescription="carousel"
          aria-label="客户证言"
          className="relative"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8% 0px" }}
          transition={{ duration: 0.55, ease: easeOut }}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              goTo(index + 1, 1);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              goTo(index - 1, -1);
            }
          }}
          tabIndex={canCycle ? 0 : undefined}
        >
          <div className="border-default-100/40 relative overflow-hidden rounded-[22px] border bg-[#07070a] shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_32px_100px_rgba(0,0,0,0.45)]">
            <AnimatePresence mode="wait" custom={direction} initial={false}>
              <m.article
                key={current.id}
                custom={direction}
                initial={{ opacity: 0, x: direction * slideOffset }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -slideOffset }}
                transition={{ duration: reduceMotion ? 0.01 : 0.38, ease: easeOut }}
                className="grid min-h-[320px] grid-cols-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(240px,0.72fr)]"
                aria-live="polite"
              >
                <div className="relative flex min-w-0 flex-col justify-between gap-10 bg-default-50 px-6 py-7 sm:gap-14 sm:px-8 sm:py-9 lg:px-10 lg:py-10">
                  <img
                    src={encodeURI(current.logoSrc)}
                    alt={current.brand}
                    className="h-7 w-auto max-w-[160px] object-contain object-left sm:h-8"
                  />
                  <blockquote className="text-pretty text-[1.05rem] font-normal leading-[1.7] tracking-[-0.015em] text-white sm:text-[1.25rem] sm:leading-[1.65]">
                    “{current.quote}”
                  </blockquote>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                    <p className="text-default-500 text-[13px] leading-relaxed sm:text-sm">
                      {current.attribution}
                    </p>
                    <a
                      href={blogUrl(current.caseHref)}
                      className="group text-foreground inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium tracking-tight transition-opacity hover:opacity-80 sm:text-sm"
                    >
                      查看完整案例
                      <ArrowUpRight
                        className="size-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        strokeWidth={1.75}
                        aria-hidden
                      />
                    </a>
                  </div>
                </div>

                <div
                  className="relative flex min-h-[220px] flex-col justify-between px-6 py-7 sm:min-h-[240px] sm:px-8 sm:py-9 lg:px-9 lg:py-10"
                  style={{ backgroundColor: current.panelColor }}
                >
                  <div>
                    <p className="text-background text-[4.25rem] font-bold leading-none tracking-[-0.04em] tabular-nums sm:text-[5.25rem]">
                      {parsedMetric && !reduceMotion ? (
                        <>
                          <span className="sr-only">{current.metric}</span>
                          <span aria-hidden="true">
                            <CountUp to={parsedMetric.value} duration={0.5} />
                            {parsedMetric.suffix}
                          </span>
                        </>
                      ) : (
                        current.metric
                      )}
                    </p>
                    <p className="text-background mt-4 max-w-[24ch] text-[15px] font-medium leading-snug sm:text-base">
                      {current.metricLabel}
                    </p>
                  </div>
                  <p className="text-background/70 text-[12px] leading-relaxed sm:text-[13px]">
                    {current.source}
                  </p>
                </div>
              </m.article>
            </AnimatePresence>
          </div>

          <div className="mt-5 flex items-center justify-center gap-4 sm:mt-6 sm:gap-5">
            <button
              type="button"
              aria-label="上一条证言"
              disabled={!canCycle}
              onClick={() => goTo(index - 1, -1)}
              className="border-default-100 text-foreground flex size-10 items-center justify-center rounded-lg border transition-[border-color,background-color] duration-300 hover:border-default-300 hover:bg-default-100/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="size-4" strokeWidth={1.75} aria-hidden />
            </button>

            <div className="flex items-center gap-2" role="tablist" aria-label="证言进度">
              {testimonials.map((item, i) => {
                const active = i === index;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-label={`查看第 ${i + 1} 条证言，共 ${count} 条`}
                    onClick={() => goTo(i, i > index ? 1 : -1)}
                    className={`h-2 rounded-full transition-[background-color,width,transform] duration-300 ${
                      active
                        ? "w-5 bg-zinc-200"
                        : "w-2 bg-zinc-600 hover:bg-zinc-500"
                    }`}
                  />
                );
              })}
            </div>

            <button
              type="button"
              aria-label="下一条证言"
              disabled={!canCycle}
              onClick={() => goTo(index + 1, 1)}
              className="border-default-100 text-foreground flex size-10 items-center justify-center rounded-lg border transition-[border-color,background-color] duration-300 hover:border-default-300 hover:bg-default-100/40 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="size-4" strokeWidth={1.75} aria-hidden />
            </button>
          </div>
        </m.div>

        <m.div
          className="border-default-100/40 relative mt-10 w-full min-w-0 overflow-hidden rounded-[22px] border bg-[#07070a] px-5 py-5 shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_32px_100px_rgba(0,0,0,0.45)] sm:mt-12 sm:px-6 sm:py-5 lg:px-8"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-6% 0px" }}
          transition={{ duration: 0.5, ease: easeOut, delay: 0.06 }}
        >
          <div
            className="pointer-events-none absolute inset-0 rounded-[22px] opacity-[0.85]"
            aria-hidden
            style={{
              background: `
                linear-gradient(180deg, rgba(255,255,255,0.055) 0%, transparent 38%),
                linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)
              `,
              backgroundSize: "100% 100%, 22px 22px, 22px 22px",
            }}
          />
          <div
            className="pointer-events-none absolute -top-[42%] left-[-8%] h-[min(72%,420px)] w-[min(88%,520px)] rounded-[50%] blur-[100px]"
            aria-hidden
            style={{
              background:
                "radial-gradient(closest-side, rgba(0, 183, 255, 0.09), transparent 72%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/[0.04]"
            aria-hidden
          />

          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <p className="text-default-500 max-w-[22ch] shrink-0 text-[13px] leading-relaxed sm:text-sm">
              {testimonialIntegrationsLead}
            </p>
            <ul className="flex min-w-0 flex-wrap items-center gap-x-0 gap-y-3">
              {testimonialIntegrations.map((item, i) => {
                const Icon = INTEGRATION_ICONS[item.icon];
                return (
                  <li
                    key={item.label}
                    className={`flex items-center gap-2 px-3 sm:px-4 ${
                      i > 0 ? "border-l border-white/[0.1]" : ""
                    }`}
                  >
                    <Icon
                      className="text-zinc-400 size-4 shrink-0"
                      strokeWidth={1.6}
                      aria-hidden
                    />
                    <span className="text-zinc-300 whitespace-nowrap text-[12px] font-medium tracking-tight sm:text-[13px]">
                      {item.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </m.div>
      </LazyMotion>
    </section>
  );
}
